import React, { memo, useCallback, useMemo } from "react";

import { modules } from "byzantine";
import { ValidQueryDescription } from "byzantine/src/dbbl/businessLogic/modules/alerts/custom";
import Account from "byzantine/src/Account";

import styles from "../CustomAlerts.module.scss";

interface PrefixProps {
  alertType: API.DeliveryChannel;
}

const Prefix = memo(({ alertType }: PrefixProps) => {
  const prefix = useMemo(() => {
    switch (alertType) {
      case "push":
        return "Notify me";
      case "email":
        return "Email me";

      case "sms":
      default:
        return "Text me";
    }
  }, [alertType]);

  return <span className="fontWeight--semibold">{prefix} </span>;
});

interface AlertDescriptionProps {
  describedQuery: ValidQueryDescription;
}

const AlertDescription = memo(({ describedQuery }: AlertDescriptionProps) => {
  const descriptionWords = describedQuery.description.split(" ");

  return (
    <>
      {describedQuery.terms.length ? (
        <span>
          {descriptionWords.map((word, index) => (
            <span
              key={index}
              className={
                word.includes("$") || word.includes('"')
                  ? "fontWeight--semibold"
                  : ""
              }
            >
              {word}
              {index < descriptionWords.length - 1 ? " " : ""}
            </span>
          ))}
        </span>
      ) : (
        <span>when a transaction matches anything</span>
      )}
    </>
  );
});

interface AccountSuffixProps {
  accountName: string;
}

const AccountSuffix = memo(({ accountName }: AccountSuffixProps) => (
  <span>
    {" "}
    for my <span className="fontWeight--semibold">{accountName}</span>
  </span>
));

const useRenderAlertTerms = (accounts: Account[]) => {
  const { describeQuery } = modules.customAlerts.useCustomAlertTerms(accounts);

  const renderTerms = useCallback(
    (alertType: API.DeliveryChannel, initialQuery: string) => {
      const describedQuery = describeQuery(initialQuery);
      if (!describedQuery.valid) {
        return <p className="fontWeight--semibold">Invalid alert</p>;
      }

      return (
        <div className={styles.termsContainer}>
          <p>
            <Prefix alertType={alertType} />
            <AlertDescription describedQuery={describedQuery} />
            {!!describedQuery.account && (
              <AccountSuffix
                accountName={describedQuery.account.getShortDescription(false)}
              />
            )}
            {"."}
          </p>
        </div>
      );
    },
    [describeQuery]
  );

  return { renderTerms };
};

export default useRenderAlertTerms;
